import * as Sentry from '@sentry/react'
import { getCurrency, getUserLanguage } from 'Lib'
import { ActionType, Selectors } from 'Reducers'
import { ICommitPaymentStep, ILoadProducts, IPurchaseEbookAction, IResolveMorEbookPurchaseAction, IUpdateSubscription, IUpdateSubscriptionOfferDecline, PaymentStep } from 'Reducers/PaymentPersistingRedux'
import { fetchStripePrices, purchaseEbookHandler, updateSubscription } from 'Services'
import { getFeatureValue, setGrowthbookAttributes } from 'Services/GrowthBook.service'
import { all, call, delay, put, select } from 'typed-redux-saga'
import { showToast } from 'Components/Toast'
import { t } from 'i18next'
import { stripeService as stripeServicePromise } from 'store'
import { PaymentIntentResult, Stripe } from '@stripe/stripe-js'
import { getSubscriptionDuration } from 'Lib/SubscriptionUtils'
import { isMorCheckoutEnabled } from 'Lib/StripeUtils'
import { push } from 'connected-react-router'
import { getDownloadUrl, getEbookPurchaseSuccessUrl, getEbookPurchaseUrl, getUpdateSubscriptionUrl, getUpgradeAccountUrl } from 'Lib/EventsUtils'
import { removeSearchParams } from 'Lib/AppUtils'
import { saveSourceMetadataSaga, waitForLogin, waitForStartup } from 'Sagas/HelperSagas'
import { takeEverySafe, takeLatestSafe } from 'Sagas/SagaUtils'

// The Elements charge knows its PaymentIntent; the MoR return carries only the checkout
// session id, which the server resolves into one.
function * handleEbookPurchaseSuccess (paymentIntentId: string, checkoutSessionId?: string) {
  // The ids stay out of the URL, so this event is where support and QA read them back.
  // It is not a third-party forwarded event, so they reach our own analytics only.
  yield call(logEvent, 'web_purchaseEbookSuccess', checkoutSessionId ? { checkoutSessionId } : { paymentIntentId })

  yield call(saveSourceMetadataSaga, paymentIntentId, 'ebook', checkoutSessionId)
}

const TRACKED_EBOOK_PURCHASES_KEY = 'trackedEbookPurchases'

// Claimed before the attribution write, since the id only leaves the URL once that
// write returns — a reload inside the window would record the purchase twice. A failed
// claim counts as unclaimed: a duplicate row beats stranding a charged buyer.
const claimEbookPurchase = (checkoutSessionId: string): boolean => {
  try {
    const tracked: string[] = JSON.parse(localStorage.getItem(TRACKED_EBOOK_PURCHASES_KEY) ?? '[]')

    if (tracked.includes(checkoutSessionId)) {
      return false
    }

    localStorage.setItem(TRACKED_EBOOK_PURCHASES_KEY, JSON.stringify([...tracked, checkoutSessionId]))
  } catch (error) {
    // logError reaches localStorage itself, so the report is best-effort here.
    try {
      logError('web_ebookPurchaseClaimError', error as Error, { checkoutSessionId }, true)
    } catch {
      // Reporting must not strand the buyer either.
    }
  }

  return true
}

// Runs on the ebook success route the MoR checkout returns to, which can land before
// auth is restored — saveSourceMetadata needs the token. The waits are unbounded; the
// container times the stall out and offers a retry.
function * resolveMorEbookPurchaseSaga ({ checkoutSessionId }: IResolveMorEbookPurchaseAction) {
  yield call(waitForStartup)
  yield call(waitForLogin)

  // A second arrival at the success route carries no id: the first one consumed it.
  if (checkoutSessionId) {
    const claimed: boolean = yield call(claimEbookPurchase, checkoutSessionId)

    if (claimed) {
      yield call(handleEbookPurchaseSuccess, '', checkoutSessionId)
    }

    // Clears the URL and the persisted store together, so the id neither reaches the
    // ad pixels that read document.location nor lingers to resolve a second time.
    yield call(removeSearchParams, ['ebook_session_id'])
  }

  // Nothing fetches the subscription on this route, so a null one here is expected.
  yield put({ type: ActionType.COMMIT_PAYMENT_STEP, stepId: 'ebook', subscriptionExpected: false })
}

function * handleSubscriptionUpsellSuccess (subscriptionId: string) {
  yield call(logEvent, 'web_updateSubscriptionSuccess')

  yield call(saveSourceMetadataSaga, subscriptionId, 'subscription_upsell')
}

export function * loadProductsSaga ({ groupId, isRetry = false }: ILoadProducts) {
  try {
    yield call(logEvent, 'web_fetchStripePrices', { groupId, isRetry })

    const currency: ReturnType<typeof getCurrency> = yield call(getCurrency)
    const { data } = yield call(fetchStripePrices, groupId, currency)

    if (data.countryCode) {
      localStorage.setItem('countryCode', data.countryCode)

      yield call(setGrowthbookAttributes, {
        country: data.countryCode
      })
    }

    localStorage.setItem('region', data.region ?? 'unknown')

    yield put({ type: ActionType.MERGE_PRODUCTS, groupId, prices: data.prices })
  } catch (error) {
    logEvent('web_fetchStripePricesError', { error, groupId, isRetry })
    Sentry.captureException(error)

    yield delay(3000)
    yield put({ type: ActionType.LOAD_PRODUCTS, groupId, isRetry: true })
  }
}

function * confirmCardPaymentSaga (clientSecret: string, stepId: PaymentStep, purchaseId: string) {
  try {
    yield call(logEvent, 'web_confirmCardPaymentRequest')

    const stripeService: Stripe = yield stripeServicePromise
    const res: PaymentIntentResult = yield call(stripeService.confirmCardPayment, clientSecret)

    if (res?.paymentIntent?.status === 'succeeded') {
      if (stepId === 'upsell') {
        yield call(handleSubscriptionUpsellSuccess, purchaseId)
      } else if (stepId === 'ebook') {
        yield call(handleEbookPurchaseSuccess, purchaseId)
      }

      yield call(logEvent, 'web_confirmCardPaymentSuccess')
      yield put({ type: ActionType.COMMIT_PAYMENT_STEP, stepId })
    } else {
      showToast('error', t('StripeError.paymentDeclinedInfo'))
      yield call(logEvent, 'web_confirmCardPaymentDeclined')
    }
  } catch (error) {
    showToast('error', t('StripeError.paymentDeclinedInfo'))
    yield call(logError, 'web_confirmCardPaymentError', error)
  }
}

export function * updateSubscriptionSaga ({ priceId, stepId }: IUpdateSubscription) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  try {
    yield call(logEvent, 'web_updateSubscriptionRequest', { stepId, priceId })

    const lang: ReturnType<typeof getUserLanguage> = yield call(getUserLanguage)
    const { subscription } = yield call(updateSubscription, priceId, false, lang)

    yield call(handleSubscriptionUpsellSuccess, subscription.id)

    yield put({ type: ActionType.COMMIT_PAYMENT_STEP, stepId: 'upsell' })
  } catch (error) {
    if (error.response?.status === 402 && error.response.data?.requires_action) {
      yield call(confirmCardPaymentSaga, error.response.data!.client_secret, 'upsell', error.response.data!.subscription.id)
    } else {
      showToast('error', t('StripeError.paymentDeclinedInfo'))
      yield call(logEvent, 'web_updateSubscriptionError', { stepId, priceId, errorMessage: error.message, errorCode: error.code })
    }
  }

  yield put({ type: ActionType.SET_APP_UNBLOCKED })
}

export function * declineUpdateSubscriptionSaga ({ stepId }: IUpdateSubscriptionOfferDecline) {
  yield call(logEvent, 'web_updateSubscriptionOfferDecline', { stepId })
  yield put({ type: ActionType.COMMIT_PAYMENT_STEP, stepId: 'upsell' })
}

export function * purchaseEbookSaga ({ priceId, currency }: IPurchaseEbookAction) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  // Stays true only while the browser is on its way to Stripe's hosted checkout, where
  // the blocked overlay is what we want. Every other exit unblocks, including saga
  // cancellation from a second tap on the CTA — otherwise the app is stuck loading.
  let redirectingToCheckout = false

  try {
    yield call(logEvent, 'web_purchaseEbookRequest')

    const successEndpoint: string = yield call(getEbookPurchaseSuccessUrl)
    const cancelEndpoint: string = yield call(getEbookPurchaseUrl)

    const response: Awaited<ReturnType<typeof purchaseEbookHandler>> = yield call(purchaseEbookHandler, priceId, currency, true, successEndpoint, cancelEndpoint)

    if (response.url) {
      // MoR buyers pay in a Stripe-hosted Managed Payments checkout. The return is
      // handled by the EbookPurchaseSuccess container via the ebook_session_id param.
      yield call(logEvent, 'web_morEbookCheckoutRedirect')
      redirectingToCheckout = true
      window.location.assign(response.url)
      return
    }

    const paymentIntent = response.paymentIntent

    if (!paymentIntent) {
      throw new Error('purchaseEbook returned neither a checkout url nor a paymentIntent')
    }

    yield call(handleEbookPurchaseSuccess, paymentIntent.id)

    yield put({ type: ActionType.COMMIT_PAYMENT_STEP, stepId: 'ebook' })
  } catch (error) {
    if (error.response?.status === 402 &&
      (error.response.data?.requires_action || error.response.data?.requires_payment_method)
    ) {
      yield call(confirmCardPaymentSaga, error.response.data!.client_secret, 'ebook', error.response.data!.paymentIntent.id)
    } else {
      showToast('error', t('StripeError.paymentDeclinedInfo'))
      yield call(logEvent, 'web_purchaseEbookError', { errorMessage: error.message, errorCode: error.code })
    }
  } finally {
    if (!redirectingToCheckout) {
      yield put({ type: ActionType.SET_APP_UNBLOCKED })
    }
  }
}

function * declineEbookPurchaseSaga () {
  yield call(logEvent, 'web_declineEbookPurchase')
  yield put({ type: ActionType.COMMIT_PAYMENT_STEP, stepId: 'ebook' })
}

function * navigateToPostPaymentRouteSaga () {
  const isRegisteredUser: boolean = yield select(Selectors.isRegisteredUser)

  const registrationUrl: string = yield call(getUpgradeAccountUrl)
  const downloadUrl: string = yield call(getDownloadUrl)

  const successEndpoint = isRegisteredUser
    ? downloadUrl
    : registrationUrl

  yield put(push(successEndpoint))
}

function * commitPaymentStepSaga ({ stepId, subscriptionExpected = true }: ICommitPaymentStep) {
  yield call(logEvent, 'web_commitPaymentStepRequest', { stepId })

  try {
    // Null when the subscription fetch has not landed (or keeps failing): the upsell
    // needs the subscription's duration, everything else here does not.
    const subscriptionDetails: IStripeSubscription | null = yield select(Selectors.getSubscriptionDetails)
    const upsellFunnelActiveDurations: Partial<Record<SubscriptionDuration, boolean>> = yield call(getFeatureValue, 'upsell-funnel-active-durations', {})
    // morManaged comes from the subscription itself, so it still holds when the
    // localStorage flag is gone (cleared storage, another device, changed IP).
    const upsellFunnelActive = !!subscriptionDetails &&
      !!upsellFunnelActiveDurations[getSubscriptionDuration(subscriptionDetails)] &&
      !isMorCheckoutEnabled() && !subscriptionDetails.morManaged

    if (!subscriptionDetails && subscriptionExpected) {
      // Skipping the upsell here is silent otherwise: the step still commits, so a
      // degraded subscription fetch would read as traffic that simply never upsold.
      yield call(logEvent, 'web_commitPaymentStepNoSubscription', { stepId })
    }

    switch (stepId) {
      case 'payment': {
        const ebookUpsellActive: boolean = yield call(getFeatureValue, 'enable-ebook-upsell', false)

        // MoR purchasers get the ebook (sold through its own Managed Payments session)
        // but skip the subscription upsell: it modifies the subscription outside MoR
        // (BodyFast as merchant), which we can't do in non-tax-registered countries.
        if (ebookUpsellActive) {
          const ebookPurchaseUrl = getEbookPurchaseUrl()
          yield put(push(ebookPurchaseUrl))
        } else if (upsellFunnelActive) {
          const updateSubscriptionUrl = getUpdateSubscriptionUrl()
          yield put(push(updateSubscriptionUrl, { stepId: 'step1' }))
        } else {
          yield call(navigateToPostPaymentRouteSaga)
        }

        break
      }

      case 'ebook': {
        if (upsellFunnelActive) {
          const updateSubscriptionUrl = getUpdateSubscriptionUrl()
          yield put(push(updateSubscriptionUrl, { stepId: 'step1' }))
        } else {
          yield call(navigateToPostPaymentRouteSaga)
        }

        break
      }

      case 'upsell': {
        yield call(navigateToPostPaymentRouteSaga)

        break
      }
    }

    yield call(logEvent, 'web_commitPaymentStepSuccess', { stepId })
  } catch (error) {
    yield call(logEvent, 'web_commitPaymentStepError', { stepId, errorMessage: error.message, errorCode: error.code })
    showToast('error', t('errors.unknownError'))
  }
}

export function * PaymentSagas() {
  yield all([
    takeEverySafe(ActionType.LOAD_PRODUCTS, loadProductsSaga), // This takes every because we might want to fetch more than 1 product group at the same time
    takeLatestSafe(ActionType.UPDATE_SUBSCRIPTION, updateSubscriptionSaga),
    takeLatestSafe(ActionType.DECLINE_UPDATE_SUBSCRIPTION_OFFER, declineUpdateSubscriptionSaga),
    takeLatestSafe(ActionType.PURCHASE_EBOOK, purchaseEbookSaga),
    takeLatestSafe(ActionType.RESOLVE_MOR_EBOOK_PURCHASE, resolveMorEbookPurchaseSaga),
    takeLatestSafe(ActionType.DECLINE_EBOOK_PURCHASE, declineEbookPurchaseSaga),
    takeLatestSafe(ActionType.COMMIT_PAYMENT_STEP, commitPaymentStepSaga),
  ])
}
